import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';

// Components
import { Avatar, Button, Clickable, Col, Icon, Row, TermsAgreement, Text } from 'components';
import { theme } from 'lib';

/*
  Pinned to the top-left of the screen (GradientBackground is the positioned
  ancestor) instead of sitting in the centered content column. Offsets match the
  backdrop's own padding on both breakpoints.
*/
const BackButtonWrap = styled.div`
  position: absolute;
  top: 40px;
  left: 24px;
  z-index: 1;

  /* isMobile in appState is innerWidth < 1024 — keep the breakpoint in sync */
  @media (max-width: 1023px) {
    top: 16px;
    left: 16px;
  }
`;

const CtaIntro = ({ otherUser, onStart, onLogin }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.user);

  const isLoggedIn = !!currentUser?._id;

  function handleBack() {
    const idx = window.history.state?.idx ?? 0;
    if (idx > 0) {
      return navigate(-1);
    }

    // fromAuth keeps the homepage from bouncing a first-time visitor on to /signup
    navigate('/', { replace: true, state: { fromAuth: true } });
  }

  return (
    <>
      <BackButtonWrap>
        <Clickable onClick={handleBack}>
          <Row
            ht="40px"
            wid="40px"
            hasBorder="1px solid rgba(255, 255, 255, 0.4)"
            hasRadius="32px"
            centerAll
            noFlex
          >
            <Icon type="arrowLeft" dimensions={16} col="#fff" />
          </Row>
        </Clickable>
      </BackButtonWrap>

      {/* top block: avatar, title/subtitle, then the start button + login link
          directly beneath them — flexes to fill space so it centers, leaving a
          gap above the avatar */}
      <Col wid="100%" centerAll>
        <Avatar user={otherUser} large showVerified />

        <Text h1 col="#fff" center marg="16px 0 0">
          {t('callToAction.sendShoutoutTitle', { name: otherUser.name })}
        </Text>

        <Text h3 col="#fff" center marg="8px 0 0">
          {t('callToAction.sendShoutoutSubtitle', { name: otherUser.name })}
        </Text>

        <Button
          big
          wid="100%"
          marg="64px 0 0"
          bg={theme.SECONDARY}
          text={t('callToAction.start')}
          onClick={onStart}
        />

        {isLoggedIn ? (
          <Text b2 col="#fff" marg="12px 0 0">
            {t('callToAction.authenticatedUserMsg', {
              name: currentUser.name || currentUser.username
            })}
          </Text>
        ) : (
          <Clickable marg="12px 0 0" onClick={onLogin}>
            <Text b2 col="#fff">
              {t('auth.alreadyHaveAccount')}{' '}
              <Text spanned b2 med col="#fff" style={{ textDecoration: 'underline' }}>
                {t('auth.logIn')}
              </Text>
            </Text>
          </Clickable>
        )}
      </Col>

      {/* pinned to the bottom of the screen; wrapped in a centered Col so the
          terms text centers instead of inheriting the parent's space-between */}
      {!isLoggedIn && (
        <Col wid="100%" centerAll noFlex>
          <TermsAgreement col="#fff" underline />
        </Col>
      )}
    </>
  );
};

export default CtaIntro;
